import { call, put, select, takeLatest } from "redux-saga/effects";
import { types, selectors, actions as softTokenActions } from "reducers/softToken";
import * as softToken from "middleware/softToken";
import * as dateUtils from "util/date";
import * as config from "util/config";
import * as secureStorageUtils from "util/secureStorage";
import { actions as notificationActions } from "reducers/notification";
// import { replace } from "connected-react-router";
import { replace } from "connected-react-router";
import getFingerPrintData from "util/vuDeviceFingerPrint";
import { getStoredOrDeviceId } from "util/device";

import * as i18n from "../util/i18n";

const sagas = [
    takeLatest(types.SOFT_TOKEN_GET_TOKEN, getToken),
    takeLatest(types.SOFT_TOKEN_GET_TOKEN_SEE, getDeviceSee),
    takeLatest(types.SOFT_TOKEN_GET_DEVICE, getDevice),
    takeLatest(types.SOFT_TOKEN_DELETE_DEVICE, deleteDevice),
    takeLatest(types.SOFT_TOKEN_REGISTER_DEVICE, registerDevice),
    takeLatest(types.SOFT_TOKEN_REGISTER_DEVICE_REPLACE, registerDeviceReplace),
    takeLatest(types.SOFT_TOKEN_REGISTER_DEVICE_PRE, registerDevicePre),
    takeLatest(types.SOFT_TOKEN_GET_DEVICE_USER, getDevicesByUser),
    takeLatest(types.SOFT_TOKEN_DELETE_DEVICE_ERROR, getDevicesByUser),
];

export default sagas;

const getOTPMobile = (seed) => window.softToken.getOTP(seed);
export const SECURITY_SEED_KEY = "softTokenSeed";
export const SECURITY_DEVICE_ID = "softTokenDeviceID";

const getDeviceExtraInfo = () => {
    let device;
    if (window.cordova) {
        const { manufacturer } = window.device;
        let { model } = window.device;
        if (model && model.startsWith("iP")) {
            model = i18n.get(`devices.apple.identifier.${model}`, model);
        }
        const uuid = window.deviceInfo.getId();

        const last4 = uuid.slice(uuid.length - 4);

        device = window.device;
        device.uuid = uuid;
        device.model = model;
        device.manufacturer = manufacturer;
        device.shortLabel = `${manufacturer} | ${model} | ${last4}`;
    }
    return device;
};

function* registerDevicePre() {
    const device = yield call(getDeviceExtraInfo);
    // const device = {
    //     uuid: "7a830100bc49829b",
    //     model: "HOLA",
    //     shortLabel: "PROMAX",
    //     manufacturer: "SD",
    // };
    yield put({
        type: types.SOFT_TOKEN_REGISTER_DEVICE_PRE_SUCCESS,
        device,
    });
}

function* registerDevice() {
    if (window.cordova) {
        const device = yield call(getDeviceExtraInfo, "");
        const fingerprintData = yield call(getFingerPrintData);
        const response = yield call(softToken.registerUserDeviceSoftToken, {
            idDevice: device?.uuid ?? "",
            extraInfo: JSON.stringify(device),
            shortLabel: device?.model ?? "",
            _fingerprintData: fingerprintData,
        });
        if (response && response.type === "I") {
            const { seed, deviceIdentification } = response.data.data;
            yield call(secureStorageUtils.set, SECURITY_SEED_KEY, seed);
            yield call(secureStorageUtils.set, SECURITY_DEVICE_ID, deviceIdentification);
            yield put(softTokenActions.registerUserDeviceSuccess(deviceIdentification));
            yield put(softTokenActions.getDeviceStatus());
            yield put({
                type: types.SOFT_TOKEN_REGISTER_DEVICE_SUCCESS,
                deviceIdentification,
            });
            yield put(softTokenActions.getDevicesByUser());
        } else {
            yield put(softTokenActions.registerUserDeviceError());
        }
    }
}
function* registerDeviceReplace({ password }) {
    if (window.cordova) {
        const device = yield call(getDeviceExtraInfo, "");
        const fingerprintData = yield call(getFingerPrintData);
        const ticketStates = {
            COR020W: "INVALID_CREDENTIAL",
            COR023I: "PENDING",
            COR000I: "FINISHED",
            API560W: "OMA_API_INTERNAL_ERROR",
        };

        const response = yield call(softToken.registerUserDeviceSoftTokenReplace, {
            idDevice: device?.uuid,
            extraInfo: JSON.stringify(device),
            shortLabel: device?.model,
            _password: password,
            _fingerprintData: fingerprintData,
        });

        const { code } = response.data;

        if (response && code === "COR000I") {
            yield put(softTokenActions.getDevicesByUser());
            const { seed, deviceIdentification } = response.data.data;
            yield call(secureStorageUtils.set, SECURITY_SEED_KEY, seed);
            yield call(secureStorageUtils.set, SECURITY_DEVICE_ID, deviceIdentification);
            yield put(softTokenActions.registerUserDeviceSuccess(deviceIdentification));
            yield put(softTokenActions.getDeviceStatus());
            yield put({
                type: types.SOFT_TOKEN_REGISTER_DEVICE_REPLACE_SUCCESS,
                deviceIdentification,
            });
        } else {
            yield put({
                type: types.SOFT_TOKEN_REGISTER_DEVICE_REPLACE_ERROR,
                deviceStatus: ticketStates[code],
            });

            yield put(softTokenActions.registerUserDeviceError());
        }
    }
}
function* getToken() {
    yield call(getTokenInfo);
}

function* getDeviceSee({ exchangeToken }) {
    if (window.cordova) {
        const device = yield call(getDeviceExtraInfo, "");
        const fingerprintData = yield call(getFingerPrintData);
        const response = yield call(
            softToken.registerUserDeviceSoftTokenOnboarding,
            {
                idDevice: device?.uuid ?? "",
                extraInfo: JSON.stringify(device),
                shortLabel: device?.model ?? "",
                _fingerprintData: fingerprintData,
            },
            exchangeToken,
        );
        if (response && response.type === "I") {
            const { seed, deviceIdentification } = response.data.data;
            yield call(secureStorageUtils.set, SECURITY_SEED_KEY, seed);
            yield call(secureStorageUtils.set, SECURITY_DEVICE_ID, deviceIdentification);
        } else {
            yield put(notificationActions.showNotification(i18n.get("onboarding.token.error"), "error", ["login"]));
        }
        yield put(replace(`/`));
    }
}
function* getDevicesByUser() {
    const response = yield call(softToken.getDevicesByUser);
    yield put(softTokenActions.registerDevicePre());
    if (response && response.type === "I") {
        const { devices } = response.data.data;
        yield put({
            type: types.SOFT_TOKEN_GET_DEVICE_USER_SUCCESS,
            devices,
        });
    }
}

function* getTokenInfo() {
    let newTokenInfo;
    if (window.cordova) {
        try {
            const counter = config.getInteger("softToken.time.counter", 30);

            const oldTokenInfo = yield select(selectors.getOldTokenInfo);
            const softTokenSeed = yield call(secureStorageUtils.get, SECURITY_SEED_KEY);
            const newToken = yield call(getOTPMobile, softTokenSeed);

            let remainingTime = 30;

            newTokenInfo = {
                token: newToken,
                tokenInitDateInMilis: Date.now(),
                remainingTime,
            };

            if (oldTokenInfo) {
                const { token, tokenInitDateInMilis } = oldTokenInfo;

                if (newToken === token) {
                    const diff = dateUtils.getSecondsBetweenDates(Date.now(), tokenInitDateInMilis);

                    remainingTime = counter - diff;
                    if (remainingTime < 0) {
                        remainingTime = 0;
                    }
                    newTokenInfo = {
                        token,
                        tokenInitDateInMilis,
                        remainingTime,
                    };
                }
            }

            yield put({
                type: types.SOFT_TOKEN_GET_TOKEN_SUCCESS,
                tokenInfo: newTokenInfo,
            });
        } catch (error) {
            yield put({
                type: types.SOFT_TOKEN_GET_TOKEN_ERROR,
                deviceStatus: "NO_DEVICE",
            });
        }
    } else {
        yield put({
            type: types.SOFT_TOKEN_GET_TOKEN_ERROR,
            deviceStatus: "NO_DEVICE",
        });
    }
    return newTokenInfo;
}

function* getDevice() {
    let registeredDeviceId = "";
    let softokenRegisteredByOtherUser = false;
    let uuid = "";
    if (window.cordova) {
        // try {
        uuid = getStoredOrDeviceId();
        registeredDeviceId = yield call(secureStorageUtils.get, SECURITY_DEVICE_ID) ?? "";
        softokenRegisteredByOtherUser = !!registeredDeviceId;
        // } catch (error) {}
    }
    const response = yield call(softToken.getUserDeviceSoftToken, registeredDeviceId ?? "", uuid ?? "");
    if (response && response.type === "I") {
        const { configuredDevice } = response.data.data;
        let device;
        let enable;
        if (configuredDevice) {
            const { extraInfo, idDevice } = configuredDevice;
            device = JSON.parse(extraInfo);
            enable = true;
            if (registeredDeviceId) {
                softokenRegisteredByOtherUser = registeredDeviceId !== idDevice;
            } else {
                softokenRegisteredByOtherUser = false;
            }
        } else {
            const { removeRegisteredDeviceId } = response.data.data;
            enable = false;
            if (!removeRegisteredDeviceId) {
                softokenRegisteredByOtherUser = true;
                yield call(secureStorageUtils.remove, SECURITY_SEED_KEY);
                yield call(secureStorageUtils.remove, SECURITY_DEVICE_ID);
            }
        }
        yield put({
            type: types.SOFT_TOKEN_GET_DEVICE_SUCCESS,
            configuredDevice: { ...device, enable },
            softokenRegisteredByOtherUser,
        });
    } else {
        yield put({
            type: types.SOFT_TOKEN_GET_DEVICE_ERROR,
        });
    }
}
function* deleteDevice({ device }) {
    const { idDevice, shortLabel, _password } = device;

    const response = yield call(softToken.deleteUserDeviceSoftToken, idDevice, shortLabel, _password);
    const { code } = response.data;
    const ticketStates = {
        COR020W: "INVALID_CREDENTIAL",
        COR023I: "PENDING",
        COR000I: "FINISHED",
        API560W: "OMA_API_INTERNAL_ERROR",
    };
    if (response && response.type === "I") {
        if (window.cordova) {
            yield call(secureStorageUtils.remove, SECURITY_SEED_KEY);
            yield call(secureStorageUtils.remove, SECURITY_DEVICE_ID);
        }
        yield put(softTokenActions.getDevicesByUser());

        yield put({
            type: types.SOFT_TOKEN_DELETE_DEVICE_SUCCESS,
            deviceIdentification: undefined,
        });
        // yield put(replace(`/settings/myToken`));
    } else {
        yield put({
            type: types.SOFT_TOKEN_DELETE_DEVICE_ERROR,
            deviceStatus: ticketStates[code],
        });
    }
}
